import type { ShapeDef, StoneShape } from '../types';

/**
 * 机巧石形状定义
 * 每个形状有多个旋转态，用 0/1 矩阵表示
 * 1 = 占据的格子, 0 = 空格
 */
export const SHAPE_DEFS: Record<StoneShape, ShapeDef> = {
    /* ===== T 形 — 标准 SRS 旋转 ===== */
    T: {
        label: 'T形',
        rotations: [
            // 旋转0: 0° (⊥) 凸起朝上
            //   X
            // XXX
            [
                [0, 1, 0],
                [1, 1, 1],
            ],
            // 旋转1: 90° (⊢) 凸起朝右 (CW)
            // X
            // XX
            // X
            [
                [1, 0],
                [1, 1],
                [1, 0],
            ],
            // 旋转2: 180° (⊤) 凸起朝下
            // XXX
            //  X
            [
                [1, 1, 1],
                [0, 1, 0],
            ],
            // 旋转3: 270° (⊣) 凸起朝左
            //  X
            // XX
            //  X
            [
                [0, 1],
                [1, 1],
                [0, 1],
            ],
        ],
    },

    /* ===== 田形 (2×2 方块) ===== */
    TIAN: {
        label: '田形',
        rotations: [
            // 2×2 方块，旋转不变
            [
                [1, 1],
                [1, 1],
            ],
        ],
    },

    /* ===== 左L (J形，钩子在左) ===== */
    L_LEFT: {
        label: '左L形',
        rotations: [
            // 旋转0: 0°
            // □ ■ □
            // □ ■ □
            // ■ ■ □
            [
                [0, 1],
                [0, 1],
                [1, 1],
            ],
            // 旋转1: 90° (CW)
            // □ □ □
            // ■ □ □
            // ■ ■ ■
            [
                [1, 0, 0],
                [1, 1, 1],
            ],
            // 旋转2: 180°
            // □ ■ ■
            // □ ■ □
            // □ ■ □
            [
                [1, 1],
                [1, 0],
                [1, 0],
            ],
            // 旋转3: 270°
            // ■ ■ ■
            // □ □ ■
            // □ □ □
            [
                [1, 1, 1],
                [0, 0, 1],
            ],
        ],
    },

    /* ===== 右L (L形，钩子在右) ===== */
    L_RIGHT: {
        label: '右L形',
        rotations: [
            // 旋转0: 0°
            // □ ■ □
            // □ ■ □
            // □ ■ ■
            [
                [1, 0],
                [1, 0],
                [1, 1],
            ],
            // 旋转1: 90° (CW)
            // ■ ■ ■
            // ■ □ □
            // □ □ □
            [
                [1, 1, 1],
                [1, 0, 0],
            ],
            // 旋转2: 180°
            // ■ ■ □
            // □ ■ □
            // □ ■ □
            [
                [1, 1],
                [0, 1],
                [0, 1],
            ],
            // 旋转3: 270°
            // □ □ □
            // □ □ ■
            // ■ ■ ■
            [
                [0, 0, 1],
                [1, 1, 1],
            ],
        ],
    },

    /* ===== I形 (1×4 长条) ===== */
    I: {
        label: 'I形',
        rotations: [
            // 旋转0: 横向 (1×4)
            [
                [1, 1, 1, 1],
            ],
            // 旋转1: 竖向 (4×1)
            [
                [1],
                [1],
                [1],
                [1],
            ],
            // 旋转2: 横向 (同旋转0)
            [
                [1, 1, 1, 1],
            ],
            // 旋转3: 竖向 (同旋转1)
            [
                [1],
                [1],
                [1],
                [1],
            ],
        ],
    },
};

/** 匠心石形状（固定为 2×2 田形） */
export const CRAFT_SHAPE: ShapeDef = {
    label: '匠心石',
    rotations: [
        [
            [1, 1],
            [1, 1],
        ],
    ],
};

/** 获取某个石头形状在指定旋转态的矩阵 */
export function getShapeMatrix(shape: StoneShape, rotation: number): number[][] {
    const def = SHAPE_DEFS[shape];
    return def.rotations[rotation % def.rotations.length];
}

/** 获取形状在当前旋转态的宽和高 */
export function getShapeSize(shape: StoneShape, rotation: number): { w: number; h: number } {
    const matrix = getShapeMatrix(shape, rotation);
    return { w: matrix[0].length, h: matrix.length };
}

/** 获取形状占据的格子坐标列表（相对于锚点） */
export function getShapeCells(shape: StoneShape, rotation: number): [number, number][] {
    const matrix = getShapeMatrix(shape, rotation);
    const cells: [number, number][] = [];
    for (let r = 0; r < matrix.length; r++) {
        for (let c = 0; c < matrix[r].length; c++) {
            if (matrix[r][c]) {
                cells.push([r, c]);
            }
        }
    }
    return cells;
}

/** 获取形状占据的格子数 */
export function getShapeCellCount(shape: StoneShape): number {
    // T形 = 4, 田形 = 4, 左L = 4, 右L = 4, 匠心石 = 4
    return 4;
}

/**
 * 获取形状锚点偏移量
 * 返回 { dr, dc } 表示形状第一个实际占据的格子相对于矩阵左上角的偏移
 * 用于修正放置时锚点位置，使鼠标点击的格子落在形状的第一个实际格子上
 * 例如 T形旋转1: [[0,1],[1,1],[0,1]] → 第一个格子是 (0,1)，偏移为 { dr: 0, dc: 1 }
 */
export function getShapeAnchorOffset(shape: StoneShape, rotation: number): { dr: number; dc: number } {
    const cells = getShapeCells(shape, rotation);
    if (cells.length === 0) return { dr: 0, dc: 0 };
    let minR = Infinity, minC = Infinity;
    for (const [r, c] of cells) {
        if (r < minR) minR = r;
        if (c < minC) minC = c;
    }
    return { dr: minR, dc: minC };
}